/**
 * Expression Evaluator
 *
 * Safely evaluates parsed AST nodes against a context.
 * No eval(), no Function(), no dynamic code execution.
 */
import { EXPRESSION_LIMITS, ALLOWED_METHODS, } from './types';
/**
 * Evaluation error
 */
export class EvaluationError extends Error {
    constructor(message) {
        super(message);
        this.name = 'EvaluationError';
    }
}
/**
 * Safe property access - no prototype chain traversal
 */
function getProperty(obj, key) {
    if (obj === null || obj === undefined) {
        return undefined;
    }
    // Block prototype chain access
    if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
        throw new EvaluationError(`Property "${key}" access is not allowed`);
    }
    if (typeof obj !== 'object') {
        return undefined;
    }
    // Only access own properties
    if (!Object.prototype.hasOwnProperty.call(obj, key)) {
        return undefined;
    }
    return obj[key];
}
/**
 * Evaluate binary operations
 */
function evaluateBinary(op, left, right) {
    switch (op) {
        // Arithmetic
        case '+': {
            if (typeof left === 'string' || typeof right === 'string') {
                return String(left) + String(right);
            }
            return left + right;
        }
        case '-':
            return left - right;
        case '*':
            return left * right;
        case '/': {
            if (right === 0) {
                return Infinity;
            }
            return left / right;
        }
        case '%':
            return left % right;
        // Comparison (strict equality only)
        case '===':
            return left === right;
        case '!==':
            return left !== right;
        case '>':
            return left > right;
        case '<':
            return left < right;
        case '>=':
            return left >= right;
        case '<=':
            return left <= right;
        // Logical
        case '&&':
            return left && right;
        case '||':
            return left || right;
        default:
            throw new EvaluationError(`Unknown operator: ${op}`);
    }
}
/**
 * Evaluate unary operations
 */
function evaluateUnary(op, arg) {
    switch (op) {
        case '!':
            return !arg;
        case '-':
            return -arg;
        case '+':
            return +arg;
        default:
            throw new EvaluationError(`Unknown unary operator: ${op}`);
    }
}
/**
 * Evaluate allowed function calls
 */
function evaluateFunction(name, args) {
    switch (name) {
        case 'Number':
            return Number(args[0]);
        case 'String':
            return String(args[0]);
        case 'Boolean':
            return Boolean(args[0]);
        case 'Math.round':
            return Math.round(args[0]);
        case 'Math.floor':
            return Math.floor(args[0]);
        case 'Math.ceil':
            return Math.ceil(args[0]);
        case 'Math.abs':
            return Math.abs(args[0]);
        case 'Math.min':
            return Math.min(...args);
        case 'Math.max':
            return Math.max(...args);
        case 'Math.pow':
            return Math.pow(args[0], args[1]);
        case 'JSON.parse': {
            if (typeof args[0] !== 'string') {
                throw new EvaluationError('JSON.parse requires a string argument');
            }
            try {
                return JSON.parse(args[0]);
            }
            catch {
                throw new EvaluationError(`JSON.parse: invalid JSON string`);
            }
        }
        case 'Array.isArray':
            return Array.isArray(args[0]);
        default:
            throw new EvaluationError(`Unknown function: ${name}`);
    }
}
/**
 * Evaluate allowed method calls on values
 */
function evaluateMethod(obj, method, args) {
    // Validate method is allowed
    if (!ALLOWED_METHODS.includes(method)) {
        throw new EvaluationError(`Method "${method}" is not allowed`);
    }
    // Handle null/undefined
    if (obj === null || obj === undefined) {
        throw new EvaluationError(`Cannot call method "${method}" on ${obj}`);
    }
    // String methods
    if (typeof obj === 'string') {
        switch (method) {
            case 'includes':
                return obj.includes(String(args[0]));
            case 'indexOf':
                return obj.indexOf(String(args[0]));
            case 'lastIndexOf':
                return obj.lastIndexOf(String(args[0]));
            case 'startsWith':
                return obj.startsWith(String(args[0]));
            case 'endsWith':
                return obj.endsWith(String(args[0]));
            case 'substring':
                return obj.substring(Number(args[0]), args[1] !== undefined ? Number(args[1]) : undefined);
            case 'slice':
                return obj.slice(Number(args[0]), args[1] !== undefined ? Number(args[1]) : undefined);
            case 'toLowerCase':
                return obj.toLowerCase();
            case 'toUpperCase':
                return obj.toUpperCase();
            case 'trim':
                return obj.trim();
            case 'split':
                return obj.split(String(args[0]));
            case 'charAt':
                return obj.charAt(Number(args[0]));
            case 'charCodeAt':
                return obj.charCodeAt(Number(args[0]));
            case 'replace':
                return obj.replace(String(args[0]), String(args[1]));
            default:
                throw new EvaluationError(`Method "${method}" is not supported on strings`);
        }
    }
    // Array methods
    if (Array.isArray(obj)) {
        switch (method) {
            case 'includes':
                return obj.includes(args[0]);
            case 'indexOf':
                return obj.indexOf(args[0]);
            case 'lastIndexOf':
                return obj.lastIndexOf(args[0]);
            case 'join':
                return obj.join(args[0] !== undefined ? String(args[0]) : ',');
            case 'slice':
                return obj.slice(Number(args[0]), args[1] !== undefined ? Number(args[1]) : undefined);
            case 'concat':
                return obj.concat(...args);
            default:
                throw new EvaluationError(`Method "${method}" is not supported on arrays`);
        }
    }
    throw new EvaluationError(`Cannot call method "${method}" on ${typeof obj}`);
}
/**
 * Evaluator class with recursion depth tracking
 */
class Evaluator {
    constructor(context) {
        this.context = context;
        this.depth = 0;
    }
    evaluate(node) {
        this.depth++;
        if (this.depth > EXPRESSION_LIMITS.MAX_DEPTH) {
            throw new EvaluationError(`Evaluation depth exceeds maximum of ${EXPRESSION_LIMITS.MAX_DEPTH}`);
        }
        try {
            return this.evaluateNode(node);
        }
        finally {
            this.depth--;
        }
    }
    evaluateNode(node) {
        switch (node.type) {
            case 'Literal':
                return node.value;
            case 'Identifier':
                // Get context root (input, output, payload)
                return this.context[node.name];
            case 'MemberExpression': {
                const obj = this.evaluate(node.object);
                return getProperty(obj, node.property);
            }
            case 'BinaryExpression': {
                // Short-circuit evaluation for && and ||
                if (node.operator === '&&') {
                    const left = this.evaluate(node.left);
                    if (!left)
                        return left;
                    return this.evaluate(node.right);
                }
                if (node.operator === '||') {
                    const left = this.evaluate(node.left);
                    if (left)
                        return left;
                    return this.evaluate(node.right);
                }
                const left = this.evaluate(node.left);
                const right = this.evaluate(node.right);
                return evaluateBinary(node.operator, left, right);
            }
            case 'UnaryExpression': {
                const arg = this.evaluate(node.argument);
                return evaluateUnary(node.operator, arg);
            }
            case 'ConditionalExpression': {
                const test = this.evaluate(node.test);
                return test ? this.evaluate(node.consequent) : this.evaluate(node.alternate);
            }
            case 'CallExpression': {
                const args = node.arguments.map((arg) => this.evaluate(arg));
                return evaluateFunction(node.callee, args);
            }
            case 'MethodCallExpression': {
                const obj = this.evaluate(node.object);
                const args = node.arguments.map((arg) => this.evaluate(arg));
                return evaluateMethod(obj, node.method, args);
            }
            default:
                throw new EvaluationError(`Unknown node type: ${node.type}`);
        }
    }
}
/**
 * Evaluate an AST node against a context
 */
export function evaluateAST(node, context) {
    const evaluator = new Evaluator(context);
    return evaluator.evaluate(node);
}
//# sourceMappingURL=evaluator.js.map